import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { RequestBenchmarkScoringBody } from '../../generated';
import { requestBenchmarkSelfScoringV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsPost } from '../../generated';
import { toBenchmarkScoring } from '../../queries/list-benchmark-scorings';
import type { BenchmarkScoring } from '../../queries/list-benchmark-scorings';

/*
 * POST /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/scorings: scores the benchmark's own images under a
 * product category. Answers `ready` when the pair is already scored and `pending` otherwise; a repeat request for a
 * pending pair returns the same scoring. The API refuses a benchmark under its `minimum_images` with
 * `BENCHMARK_TOO_SMALL`. Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

export type RequestBenchmarkScoringMutationVariables = {
  workspace_id: string;
  benchmark_id: string;
} & RequestBenchmarkScoringBody;

export type RequestBenchmarkScoringMutation = BenchmarkScoring;

async function requestBenchmarkScoring({
  workspace_id,
  benchmark_id,
  ...body
}: RequestBenchmarkScoringMutationVariables) {
  const { data } = await requestBenchmarkSelfScoringV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsPost({
    path: { workspace_id, benchmark_id },
    body,
    throwOnError: true,
  });
  return toBenchmarkScoring(data);
}

export const useRequestBenchmarkScoringMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<RequestBenchmarkScoringMutation, TError, RequestBenchmarkScoringMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<RequestBenchmarkScoringMutation, TError, RequestBenchmarkScoringMutationVariables, TContext>({
    mutationKey: ['RequestBenchmarkScoring'],
    mutationFn: requestBenchmarkScoring,
    ...options,
  });
};

useRequestBenchmarkScoringMutation.fetcher = (variables: RequestBenchmarkScoringMutationVariables) => () =>
  requestBenchmarkScoring(variables);
